import { Link, useNavigate } from 'react-router-dom';
import { Button, Chip, Spinner, Table } from '@heroui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useClientsPage } from './useClientsPage';
import type { Client } from '@/lib/types';
import { LEGAL_FORM_LABELS } from '@/lib/types';
import { useUserStore } from '@/store/userSlice';
import { PageContainer } from '@/components/templates/PageContainer';

const columnHelper = createColumnHelper<Client>();

export function ClientsPage() {
  const { clients, isLoading, error } = useClientsPage();
  const isStaff = useUserStore((s) => s.isStaff);
  const navigate = useNavigate();

  const columns = [
    columnHelper.accessor('title', {
      header: 'Организация',
      cell: (info) => {
        const client = info.row.original;
        return (
          <Link to={`/clients/${client.id}`} className="flex flex-col hover:underline">
            <span className="text-sm font-medium text-link">{client.title}</span>
            <span className="text-xs text-muted">
              {LEGAL_FORM_LABELS[client.legalForm]}
              {client.phone ? ` · ${client.phone}` : ''}
            </span>
          </Link>
        );
      },
    }),
    columnHelper.accessor((row) => row.workerCount ?? 0, {
      id: 'workers',
      header: 'Сотрудники',
      cell: (info) => {
        const count = info.getValue();
        return <span className="text-sm">{count > 0 ? count : '—'}</span>;
      },
    }),
    columnHelper.accessor('generalDirector', {
      header: 'Директор',
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('enabled', {
      header: 'Статус',
      cell: (info) => (
        <Chip color={info.getValue() ? 'success' : 'danger'} size="sm" variant="soft">
          {info.getValue() ? 'Активен' : 'Неактивен'}
        </Chip>
      ),
    }),
  ];

  const table = useReactTable({
    columns,
    data: clients,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!isStaff) return null;

  return (
    <PageContainer>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-foreground">Клиенты</h2>
        <Button variant="outline" onPress={() => navigate('/clients/new')}>
          <Plus className="size-4" />
          Новый клиент
        </Button>
      </div>

      {error && (
        <p className="mb-3 rounded-[var(--radius)] bg-danger px-3 py-2 text-sm text-danger-foreground">
          {error}
        </p>
      )}

      <Table className="min-h-[280px]">
        <Table.ScrollContainer>
          <Table.Content aria-label="Список клиентов" className="min-w-[680px]">
            <Table.Header>
              {table.getHeaderGroups()[0]!.headers.map((header) => (
                <Table.Column key={header.id} id={header.id} isRowHeader={header.id === 'title'}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Table.Column>
              ))}
            </Table.Header>
            <Table.Body
              renderEmptyState={() =>
                isLoading ? (
                  <div className="flex w-full items-center justify-center gap-2 py-10 text-sm text-muted">
                    <Spinner size="sm" />
                    Загрузка…
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-center py-10 text-sm text-muted">
                    Клиенты не найдены
                  </div>
                )
              }
            >
              {table.getRowModel().rows.map((row) => (
                <Table.Row
                  key={row.id}
                  id={row.id}
                  className="cursor-pointer"
                  onAction={() => navigate(`/clients/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
    </PageContainer>
  );
}
